import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatDateTime } from "../../../util/function/function";
import ConfirmDialog from "components/confirm_dialog";
import OnlyConfirmDialog from "components/onlyConfirm_dialog";
import { deleteInquiry, getInquiryById } from "firebase_/firebase_function/firebase_inquiry";
import OnlyLoadingScreen from "components/loading_screen";

const C = {
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  bg: "#F7FAF7",
  amber50: "#FFFBEB",
  amber800: "#92400E",
  blue50: "#EFF6FF",
  blue700: "#1D4ED8",
  red: "#EF4444",
};

const CATEGORY_EMOJI = {
  "트랙터": "🚜",
  "이앙기": "🌾",
  "콤바인": "🌾",
  "전기": "⚡",
  "엔진": "⚙️",
  "배터리": "🔋",
  "유압": "🛢️",
  "농약기": "🌱",
  "기타": "🔧",
  "전체선택": "📦",
};

function StatusChip({ status }) {
  const isDone = String(status) === "1";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 14px",
        borderRadius: 9999,
        fontSize: 12,
        fontWeight: 700,
        background: isDone ? C.greenBg : C.amber50,
        color: isDone ? C.greenDark : C.amber800,
        border: `1px solid ${isDone ? C.greenBorder : "#FDE68A"}`,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 9999,
          background: isDone ? C.greenDark : C.amber800,
        }}
      />
      {isDone ? "답변 완료" : "답변 대기"}
    </span>
  );
}

function SkeletonDetail() {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 16,
        padding: "28px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ height: 14, width: "20%", background: "#F3F4F6", borderRadius: 6 }} />
      <div style={{ height: 24, width: "70%", background: "#F3F4F6", borderRadius: 6 }} />
      <div style={{ height: 12, width: "30%", background: "#F3F4F6", borderRadius: 6 }} />
      <div style={{ height: 1, background: C.line, margin: "8px 0" }} />
      <div style={{ height: 14, width: "100%", background: "#F3F4F6", borderRadius: 6 }} />
      <div style={{ height: 14, width: "90%", background: "#F3F4F6", borderRadius: 6 }} />
      <div style={{ height: 14, width: "60%", background: "#F3F4F6", borderRadius: 6 }} />
    </div>
  );
}

export default function DesktopInquiryDetailScreen() {
  const navigate = useNavigate();
  const { docId } = useParams();

  const [pageLoading, setPageLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const [showPopup, setShowPopup] = useState(false);
  const menuWrapperRef = useRef(null);
  const popupRef = useRef(null);

  const [onlyConfirmDialog, setOnlyConfirmDialog] = useState(false);
  const [message, setMessage] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);

  const [inquiry, setInquiry] = useState({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showPopup && popupRef.current && !popupRef.current.contains(e.target)) {
        setShowPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPopup]);

  useEffect(() => {
    fetchInquiry();
  }, []);

  const fetchInquiry = async () => {
    if (!docId) return;
    setLoading(true);
    const response = await getInquiryById(docId);
    if (response) setInquiry(response);
    setLoading(false);
  };

  const pressedDeletePost = async () => {
    setPageLoading(true);
    const result = await deleteInquiry(docId);
    if (result) {
      navigate("/inquiry");
    } else {
      setOnlyConfirmDialog(true);
      setMessage("문의글 삭제에 실패했습니다.");
    }
    setPageLoading(false);
  };

  const emoji = CATEGORY_EMOJI[inquiry?.type] || "💬";
  const isDone = String(inquiry?.status) === "1";

  return (
    <div
      style={{
        fontFamily:
          'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
        maxWidth: 1100,
        margin: "0 auto",
        padding: "32px 20px 80px",
        minHeight: "100vh",
        background: C.bg,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      {/* Back link */}
      <button
        type="button"
        onClick={() => navigate("/inquiry")}
        style={{
          alignSelf: "flex-start",
          background: "transparent",
          border: "none",
          color: C.sub,
          fontSize: 13,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: 0,
        }}
      >
        ← 문의 내역으로
      </button>

      {loading ? (
        <SkeletonDetail />
      ) : (
        <>
          {/* Inquiry card */}
          <div
            style={{
              background: "#fff",
              border: `1px solid ${C.line}`,
              borderRadius: 16,
              padding: "24px 24px 20px",
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            {/* Top row: emoji + status + menu */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 12,
                    background: C.greenBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 26,
                  }}
                >
                  {emoji}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    {inquiry?.type && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: C.blue700,
                          background: C.blue50,
                          padding: "3px 10px",
                          borderRadius: 6,
                        }}
                      >
                        {inquiry.type}
                      </span>
                    )}
                    <StatusChip status={inquiry?.status} />
                  </div>
                  <div style={{ fontSize: 12, color: C.sub }}>
                    {inquiry?.createDate ? formatDateTime(inquiry.createDate) : ""}
                  </div>
                </div>
              </div>

              <div
                onClick={() => setShowPopup((p) => !p)}
                ref={menuWrapperRef}
                style={{ cursor: "pointer", position: "relative", padding: 6 }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                  }}
                >
                  <span style={{ width: 4, height: 4, borderRadius: 9999, background: C.sub }} />
                  <span style={{ width: 4, height: 4, borderRadius: 9999, background: C.sub }} />
                  <span style={{ width: 4, height: 4, borderRadius: 9999, background: C.sub }} />
                </div>
                {showPopup && (
                  <div
                    ref={popupRef}
                    style={{
                      position: "absolute",
                      top: "100%",
                      right: 0,
                      marginTop: 6,
                      background: "#fff",
                      border: `1px solid ${C.line}`,
                      borderRadius: 10,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                      zIndex: 100,
                      whiteSpace: "nowrap",
                      minWidth: 100,
                    }}
                  >
                    <div
                      style={{
                        padding: "10px 14px",
                        cursor: "pointer",
                        fontSize: 13,
                        color: C.red,
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        setShowPopup(false);
                        setDeleteModal(true);
                      }}
                    >
                      삭제하기
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Title */}
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: C.ink,
                letterSpacing: "-0.02em",
                lineHeight: 1.4,
                wordBreak: "break-word",
              }}
            >
              {inquiry?.title}
            </div>

            <div style={{ height: 1, background: C.line }} />

            {/* Body */}
            <div
              style={{
                fontSize: 15,
                color: C.body,
                lineHeight: 1.7,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                minHeight: 80,
              }}
            >
              {inquiry?.body}
            </div>
          </div>

          {/* Answer card */}
          <div
            style={{
              background: "#fff",
              border: `1px solid ${C.line}`,
              borderRadius: 16,
              padding: "20px 24px 22px",
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 9999,
                  background: isDone ? C.greenBg : "#F3F4F6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                }}
              >
                💡
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em" }}>
                답변
              </div>
              <div style={{ flex: 1 }} />
              {isDone && (
                <div style={{ fontSize: 12, color: C.sub, fontWeight: 600 }}>
                  운영자
                </div>
              )}
            </div>

            {!isDone || !inquiry?.comment ? (
              <div
                style={{
                  padding: "20px 16px",
                  background: C.bg,
                  borderRadius: 12,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 6,
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 28 }}>⏳</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>
                  답변 준비 중입니다
                </div>
                <div style={{ fontSize: 12, color: C.sub }}>
                  운영자가 확인 후 빠른 시일 내 답변드리겠습니다.
                </div>
              </div>
            ) : (
              <div
                style={{
                  padding: "16px 18px",
                  background: C.greenBg,
                  border: `1px solid ${C.greenBorder}`,
                  borderRadius: 12,
                  fontSize: 15,
                  color: C.body,
                  lineHeight: 1.7,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {inquiry.comment}
              </div>
            )}
          </div>
        </>
      )}

      <OnlyConfirmDialog
        open={onlyConfirmDialog}
        onClose={() => setOnlyConfirmDialog(false)}
        message={message}
      />
      <ConfirmDialog
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={() => {
          pressedDeletePost();
          setDeleteModal(false);
        }}
        message={"삭제 하시겠습니까?"}
      />
      <OnlyLoadingScreen isOpen={pageLoading} />
    </div>
  );
}
